import _ from 'lodash';

import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { ensureArray } from 'BMapsUtil/js_utils';
import { JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { fetchPropertyGroupWithJobman } from 'BMapsSrc/jobs/JobProperties';
import { PropApplicability } from 'BMapsModel/ScopedPropertyGroup';
import { Compound } from 'BMapsModel';
import { createJob } from 'BMapsRedux/jobs/access';
import { calculateDockingBox } from 'BMapsSrc/data_tools';
import { Directionality, preparePropertyGroupMetadata, UnitType } from './properties_common';

// On property explanation, from David Koes (https://github.com/gnina/gnina/issues/259)
// * CNN_VS is CNNaffinity*CNN_score
// * CNNaffinity is a binding affinity prediction in pK units (e.g. 9 is 1 nM)
// * CNNscore is a predicted probability that the pose is good (<2A from correct)
// * minimizedAffinity is the Vina score

const gninaProps = {
    // Empirically-based
    affinity: {
        label: 'Affinity (AutoDock Vina)',
        description: 'Predicted binding affinity from AutoDock Vina scoring function',
        unit: 'kcal/mol',
        directionality: Directionality.MINIMIZE,
    },
    // ML-based
    cnn_score: {
        label: 'Gnina Pose Score',
        description: "Gnina's predicted probability that the pose is within 2Å of the correct binding pose",
        unit: 'Probability',
        directionality: Directionality.MAXIMIZE,
    },
    cnn_affinity: {
        label: 'Gnina Affinity (CNN)',
        description: "Gnina's prediction of binding affinity in pK units (6 = 1 µM, 9 = 1 nM)",
        unit: 'pK',
        directionality: Directionality.MAXIMIZE,
    },
    cnn_variance: {
        label: 'Gnina CNN Variance',
        description: "Gnina's report of the variance/uncertainty of the CNN affinity scoring, a measure of confidence in the prediction",
        unit: 'pK Variance',
        unitType: UnitType.GTE_ZERO,
        directionality: Directionality.MINIMIZE,
    },
    cnn_vs: {
        label: 'Gnina VS',
        description: "Gnina's virtual screening score (Gnina affinity * Gnina pose score)",
        unit: '(composite)',
        directionality: Directionality.MAXIMIZE,
    },
    // From docking
    sourceSpec: {
        label: 'Gnina docking source cmpd',
        description: 'Name of the input ligand to gnina docking',
    },
};

export const gninaMetadata = preparePropertyGroupMetadata({
    groupMeta: {
        propApplicability: PropApplicability.PERPOSE,
        label: 'Gnina properties',
    },
    tooltipHeading: 'Gnina properties',
    propertyInfo: gninaProps,
});

export async function getGninaScoringProperties(compounds, { trigger }={}) {
    const chunks = chunkGninaCompounds(compounds);

    const gninaPromises = chunks.map(async (chunkCmpds) => {
        const { requestData, error } = await makeGninaRequest(chunkCmpds, { score_only: true });

        /**
         * getValues is called by internal machinery to assign properties
         * @param {*} results all job results reported by job manager for this request
         * @param {*} cmpd compound to extract values for, from the job results
         * @returns { { [propName: string]: { value: any, error: string } } } values for this
         */
        const getValues = (results, cmpd) => {
            const cmpdIndex = chunkCmpds.indexOf(cmpd);
            const { name, ...scores } = results?.jobResults?.scores?.[cmpdIndex] || {};
            if (name !== cmpd.resSpec && !cmpd.isLigand()) {
                console.warn(`gnina scores for ${name} don't match expected compound ${cmpd.resSpec} (${cmpdIndex}/${chunkCmpds.length})`);
            }
            return Object.entries(scores).reduce((acc, [propName, value]) => ({
                ...acc,
                [propName]: { value },
            }), {});
        };
        if (requestData) {
            const request = {
                jobtype: JobTypes.GNINA_SCORING,
                data: requestData,
            };
            await fetchPropertyGroupWithJobman(
                chunkCmpds, JobTypes.GNINA_SCORING, request, { trigger, getValues },
            );
        }
    });
    await Promise.all(gninaPromises);
}

export async function startGninaDocking(compounds, { boxParams, speedArg, ...otherParams }={}) {
    if (!boxParams && compounds.length > 0) {
        return [{ error: 'No box parameters provided for gnina docking' }];
    }

    const chunks = chunkGninaCompounds(compounds);

    const gninaPromises = chunks.map(async (chunkCmpds) => {
        let options = {};
        const wholeProteinDocking = !boxParams.refObj;
        if (!wholeProteinDocking) {
            const boxes = chunkCmpds.map((c) => (calculateDockingBox(App.Workspace, c, boxParams)));
            if (!boxes.every((b) => b.centroid.toString() === boxes[0].centroid.toString())) {
                console.warn(`startGninaDocking: different docking box centroids for compounds in same gnina request: ${boxes.map((b) => b.centroid).join('; ')}`);
            }
            const maxBox = _.maxBy(boxes, (b) => b.boxSize);
            const [cx, cy, cz] = maxBox.centroid;
            const edge = maxBox.boxSize;
            options = {
                center_x: cx, center_y: cy, center_z: cz, size_x: edge, size_y: edge, size_z: edge,
            };
        }
        const exhaustivenessMap = wholeProteinDocking
            ? { accurate: 128, fast: 64, faster: 8 }
            : { accurate: 64, /* fast: 8, */ faster: 4 }; // use gnina default (8) for fast
        options.exhaustiveness = exhaustivenessMap[speedArg];
        const otherGninaParams = ['minimize', 'scoring', 'cnn_scoring', 'cnn'];
        for (const gp of otherGninaParams) {
            const gpValue = otherParams[gp];
            if (gpValue != null && gpValue !== '') {
                options[gp] = gpValue;
            }
        }
        options.num_modes = otherParams.poseCount;
        const { requestData, error: requestError } = await makeGninaRequest(chunkCmpds, options);
        if (requestError) {
            return { jobResults: { error: requestError } };
        }
        const stash = {
            cmpdSmiles: chunkCmpds.reduce((acc, c) => {
                acc[c.resSpec] = { smiles: c.getUnnamedSmiles() };
                return acc;
            }, {}),
        };
        return {
            // { results, error }
            jobResults: await createJob(JobTypes.GNINA_DOCKING, requestData, stash),
            dockedCompounds: chunkCmpds,
        };
    });
    return Promise.all(gninaPromises);
}

/**
 * Chunk an array of compounds as an array of arrays,
 * ensuring that each chunk all has the same parent protein.
 * @param {Compound | Compound[]} compoundsIn
 * @returns {Compound[][]}
 */
function chunkGninaCompounds(compoundsIn, chunkSize=10) {
    const compounds = ensureArray(compoundsIn);
    const { dataParentsMap } = App.partitionByDataParents(compounds);
    return [...dataParentsMap.values()].flatMap((group) => _.chunk(group, chunkSize));
}

async function makeGninaRequest(compoundsIn, other) {
    const compounds = ensureArray(compoundsIn);
    const compound = compounds[0];
    if (!compound) {
        return { error: 'no compound provided for gnina scoring request' };
    }
    const { dataParentsList } = App.partitionByDataParents(compounds);
    if (dataParentsList.length > 1) {
        return { error: 'makeGninaRequest needs all compounds from the same protein structure' };
    }
    const { pdb, error, pdbObj } = await UserActions.GetProteinPdb(compound);
    if (pdbObj.residues.length === 0) {
        return { error: `no protein available for gnina scoring of ${compound.resSpec}` };
    } else if (error) {
        return { error };
    } else if (pdb) {
        const requestData = {
            receptor_pdb: pdb,
            ligand_sdf: Compound.makeSDF(
                compounds,
                { propInclusion: 'custom', customPropFn: (c) => ({ bmapsResSpec: c.resSpec }) },
            ),
            ...other,
        };
        return { requestData };
    } else {
        return { error: `no pdb data produced for ${compound.resSpec}` };
    }
}
